import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaHome, 
  FaFilePdf, 
  FaImage, 
  FaVideo, 
  FaCog, 
  FaSignOutAlt,
  FaChartBar,
  FaEnvelope,
  FaUsers,
  FaNewspaper
} from 'react-icons/fa';
import PageTransition from '../components/common/PageTransition';
import AdminPanel from '../components/admin/AdminPanel';
import PDFUpload from '../components/admin/PDFUpload';
import ImageUpload from '../components/admin/ImageUpload';
import VideoLinkManager from '../components/admin/VideoLinkManager';
import ContentManager from '../components/admin/ContentManager';
import AdminSettings from '../components/admin/AdminSettings';
import SEO from '../components/common/SEO';

const Admin = () => {
  const [activeTab, setActiveTab] = useState('dashboard');

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: FaHome },
    { id: 'samples', label: 'PDF Samples', icon: FaFilePdf },
    { id: 'gallery', label: 'Gallery', icon: FaImage },
    { id: 'videos', label: 'Videos', icon: FaVideo },
    { id: 'content', label: 'Content', icon: FaNewspaper },
    { id: 'settings', label: 'Settings', icon: FaCog },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <AdminDashboard />;
      case 'samples':
        return <PDFUpload />;
      case 'gallery':
        return <ImageUpload />;
      case 'videos':
        return <VideoLinkManager />;
      case 'content':
        return <ContentManager />;
      case 'settings':
        return <AdminSettings />;
      default:
        return <AdminDashboard />;
    }
  };

  return (
    <PageTransition>
      <SEO
        title="Admin Dashboard"
        description="Admin dashboard for managing The Tiny Times website content, samples, and settings."
        noIndex={true}
      />
      <div className="min-h-screen bg-neutral-gray">
        <div className="flex">
          {/* Sidebar */}
          <aside className="w-64 bg-primary-green-dark min-h-screen fixed left-0 top-0 pt-20 hidden lg:block">
            <div className="p-4">
              <div className="text-center mb-8">
                <div className="text-5xl mb-2">🛠️</div>
                <h2 className="text-white font-display font-bold text-xl">
                  Admin Panel
                </h2>
                <p className="text-white/60 text-sm">
                  Manage your content
                </p>
              </div>

              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <motion.button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      activeTab === tab.id
                        ? 'bg-white text-primary-green-dark'
                        : 'text-white/80 hover:bg-white/10'
                    }`}
                  >
                    <tab.icon className="text-xl" />
                    <span className="font-display font-semibold">{tab.label}</span>
                  </motion.button>
                ))}
              </nav>

              {/* Logout Button */}
              <div className="mt-8 pt-8 border-t border-white/20">
                <button
                  onClick={() => {
                    sessionStorage.removeItem('adminAuth');
                    window.location.href = '/';
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:bg-red-500/20 hover:text-red-300 transition-all"
                >
                  <FaSignOutAlt className="text-xl" />
                  <span className="font-display font-semibold">Logout</span>
                </button>
              </div>
            </div>
          </aside>

          {/* Mobile Header */}
          <div className="lg:hidden fixed top-0 left-0 right-0 bg-primary-green-dark z-40 px-4 py-3">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-display font-bold text-lg">
                🛠️ Admin Panel
              </h2>
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                className="bg-white/10 text-white border border-white/20 rounded-lg px-3 py-2 font-display"
              >
                {tabs.map((tab) => (
                  <option key={tab.id} value={tab.id} className="text-black">
                    {tab.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Main Content */}
          <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
            <div className="p-4 md:p-8">
              {/* Page Header */}
              <div className="mb-8">
                <h1 className="text-3xl md:text-4xl font-display font-bold text-primary-green-dark">
                  {tabs.find(t => t.id === activeTab)?.label}
                </h1>
                <p className="text-neutral-gray-dark mt-2">
                  {getTabDescription(activeTab)}
                </p>
              </div>

              {/* Content Area */}
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {renderContent()}
              </motion.div>
            </div>
          </main>
        </div>
      </div>
    </PageTransition>
  );
};

// Admin Dashboard Component
const AdminDashboard = () => {
  const stats = [
    { icon: FaFilePdf, label: 'PDF Samples', value: '4', color: 'bg-blue-500' },
    { icon: FaImage, label: 'Gallery Images', value: '12', color: 'bg-green-500' },
    { icon: FaVideo, label: 'Videos', value: '8', color: 'bg-red-500' },
    { icon: FaUsers, label: 'Subscribers', value: '1,250', color: 'bg-purple-500' },
    { icon: FaEnvelope, label: 'Messages', value: '45', color: 'bg-orange-500' },
    { icon: FaChartBar, label: 'Page Views', value: '15.2K', color: 'bg-pink-500' },
  ];

  const recentActivities = [
    { action: 'New PDF uploaded', item: 'Micro Edition Week 5', time: '2 hours ago', icon: '📄' },
    { action: 'Image added to gallery', item: 'Reader photo #45', time: '5 hours ago', icon: '🖼️' },
    { action: 'New video published', item: 'Story Time Episode 12', time: '1 day ago', icon: '🎥' },
    { action: 'Subscriber milestone', item: 'Reached 1,250 subscribers!', time: '2 days ago', icon: '🎉' },
    { action: 'Content updated', item: 'About page refreshed', time: '3 days ago', icon: '✏️' },
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Card */}
      <div className="card bg-gradient-to-r from-primary-green to-primary-green-light text-white">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="text-8xl">👋</div>
          <div>
            <h2 className="text-3xl font-display font-bold mb-2">
              Welcome back, Admin!
            </h2>
            <p className="text-white/90">
              Here's what's happening with The Tiny Times today.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card flex items-center gap-4"
          >
            <div className={`w-14 h-14 ${stat.color} rounded-xl flex items-center justify-center`}>
              <stat.icon className="text-2xl text-white" />
            </div>
            <div>
              <div className="text-3xl font-display font-bold text-primary-green-dark">
                {stat.value}
              </div>
              <div className="text-neutral-gray-dark text-sm">
                {stat.label}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="card">
          <h3 className="text-xl font-display font-bold text-primary-green-dark mb-4">
            Quick Actions ⚡
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Upload PDF', icon: '📄', action: 'samples' },
              { label: 'Add Image', icon: '🖼️', action: 'gallery' },
              { label: 'Add Video', icon: '🎥', action: 'videos' },
              { label: 'Edit Content', icon: '✏️', action: 'content' },
            ].map((item, index) => (
              <motion.button
                key={index}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center gap-3 p-4 bg-accent-blue/10 rounded-xl hover:bg-accent-blue/20 transition-colors"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="font-display font-semibold text-primary-green-dark">
                  {item.label}
                </span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <h3 className="text-xl font-display font-bold text-primary-green-dark mb-4">
            Recent Activity 📊
          </h3>
          <div className="space-y-4">
            {recentActivities.map((activity, index) => (
              <div
                key={index}
                className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0 last:pb-0"
              >
                <span className="text-2xl">{activity.icon}</span>
                <div className="flex-1">
                  <div className="font-semibold text-primary-green-dark text-sm">
                    {activity.action}
                  </div>
                  <div className="text-neutral-gray-dark text-sm">
                    {activity.item}
                  </div>
                </div>
                <div className="text-xs text-neutral-gray-dark">
                  {activity.time}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Help Card */}
      <div className="card bg-gradient-to-r from-accent-yellow/20 to-accent-orange/20 border-2 border-accent-orange/30">
        <div className="flex flex-col md:flex-row items-center gap-4">
          <div className="text-6xl">💡</div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-xl font-display font-bold text-primary-green-dark mb-1">
              Need Help?
            </h3>
            <p className="text-neutral-gray-dark">
              Check out the documentation or contact support for assistance.
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn-secondary"
          >
            View Docs
          </motion.button>
        </div>
      </div>
    </div>
  );
};

// Helper function
const getTabDescription = (tab) => {
  const descriptions = {
    dashboard: 'Overview of your content and statistics',
    samples: 'Upload and manage PDF sample issues',
    gallery: 'Manage gallery images and photos',
    videos: 'Add and edit YouTube video links',
    content: 'Edit website content and text',
    settings: 'Configure website settings',
  };
  return descriptions[tab] || '';
};

export default Admin;